import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Select, Spin, message } from 'antd';
import { ArrowLeftOutlined, QuestionCircleOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';
import TopNav from '../components/top-nav';
import FooterDisplay from '../components/footer';
import { AdminCollapseList, buildAdminCollapseItem } from '../components/admin/AdminCollapseItem';
import { _accept_group_account, _reject_group_account, _add_group_member, _create_group_account, _get_group_accounts, _request_group_withdrawal, _convert_group_wallet_to_fourward, _get_group_account_history } from '../common/axios_services';

const GroupAccountPage = () => {
  const [view, setView] = useState('list');
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('');
  const [memberId, setMemberId] = useState('');
  const [role, setRole] = useState('MEMBER');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalDestination, setWithdrawalDestination] = useState('PERSONAL_WALLET');
  const [conversionAmount, setConversionAmount] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [groupHistory, setGroupHistory] = useState([]);

  const loadGroups = async () => {
    try {
      const response = await _get_group_accounts();
      const list = response?.data?.data || response?.data || [];
      const normalizedList = Array.isArray(list) ? list : [];
      setGroups(normalizedList);
      return normalizedList;
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to load group accounts.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGroups(); }, []);

  const createGroup = async () => {
    const groupName = name.trim();
    if (!groupName) {
      message.error('Enter a group account name.');
      return;
    }
    try {
      const response = await _create_group_account(groupName);
      message.success('Group account created.');
      setName('');
      const updatedGroups = await loadGroups();
      const createdGroupId = response?.data?.data?.group?.id || response?.data?.group?.id;
      const createdGroup = updatedGroups.find((item) => (
        (createdGroupId && item?.group?.id === createdGroupId) || item?.group?.name === groupName
      ));
      setSelectedGroup(createdGroup || updatedGroups[updatedGroups.length - 1] || null);
      setView('detail');
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to create group account.');
    }
  };

  const refreshSelectedGroup = async (groupId) => {
    const updatedGroups = await loadGroups();
    setSelectedGroup(updatedGroups.find((item) => item?.group?.id === groupId) || selectedGroup);
    try {
      const response = await _get_group_account_history(groupId);
      setGroupHistory(response?.data?.data || response?.data || []);
    } catch (error) {
      setGroupHistory([]);
    }
  };

  const addMember = async () => {
    if (!selectedGroup || !memberId.trim()) return;
    try {
      await _add_group_member(selectedGroup.group.id, memberId.trim(), role);
      message.success('Member invitation sent.');
      setMemberId('');
      await refreshSelectedGroup(selectedGroup.group.id);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to add member.');
    }
  };

  const requestWithdrawal = async () => {
    if (!selectedGroup) return;
    try {
      await _request_group_withdrawal(selectedGroup.group.id, Number(withdrawalAmount), withdrawalDestination);
      message.success('Withdrawal request submitted for approval.');
      setWithdrawalAmount('');
      await refreshSelectedGroup(selectedGroup.group.id);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to submit withdrawal request.');
    }
  };

  const convertWalletToFourward = async () => {
    if (!selectedGroup || !conversionAmount) return;
    try {
      await _convert_group_wallet_to_fourward(selectedGroup.group.id, Number(conversionAmount));
      message.success('Group wallet funds moved to Group 4Ward.');
      setConversionAmount('');
      await refreshSelectedGroup(selectedGroup.group.id);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to move funds to Group 4Ward.');
    }
  };

  const openGroup = (group) => {
    setSelectedGroup(group);
    setView('detail');
    _get_group_account_history(group.group.id)
      .then((response) => setGroupHistory(response?.data?.data || response?.data || []))
      .catch(() => setGroupHistory([]));
  };

  const closeGroup = () => {
    setSelectedGroup(null);
    setView('list');
  };

  const pendingGroups = groups.filter((item) => item?.membership?.status === 'PENDING');
  const activeGroups = groups.filter((item) => item?.membership?.status === 'ACCEPTED');

  const respondToInvite = async (groupId, action) => {
    try {
      if (action === 'accept') {
        await _accept_group_account(groupId);
        message.success('Group membership accepted.');
      } else {
        await _reject_group_account(groupId);
        message.success('Group invitation rejected.');
      }
      await loadGroups();
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to update this invitation.');
    }
  };

  if (loading) return <div className="center-content"><Spin /></div>;

  return (
    <div className="wise9ja-content min-h-screen pb-40 pt-28">
      <TopNav title="Group Account" />
      <main className="contain space-y-5">
        {view === 'list' ? (
          <>
            {pendingGroups.length > 0 && (
              <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-4" role="status">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-gilroyBold text-gray-900">Pending Invitations</h2>
                    <p className="text-gray-600 text-[13px] mt-1">You have group account invitations waiting for your response.</p>
                  </div>
                  <span className="min-w-8 h-8 px-2 rounded-full bg-amber-500 text-white flex items-center justify-center font-gilroyBold" aria-label={`${pendingGroups.length} pending invitations`}>
                    {pendingGroups.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {pendingGroups.map((item) => (
                    <div key={item.group.id} className="bg-white border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-gilroyBold text-gray-900 m-0">{item.group.name}</p>
                        <p className="text-gray-500 text-[13px] m-0 mt-1">Account {item.group.accountNumber} · Invited as {item.membership?.role || 'MEMBER'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="primary" onClick={() => respondToInvite(item.group.id, 'accept')} className="bg-green-600">Accept</Button>
                        <Button danger onClick={() => respondToInvite(item.group.id, 'reject')}>Reject</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                <h2 className="text-2xl font-gilroyBold">Konnect Group Accounts 👨‍👩‍👧‍👦💰</h2>
                <p className="text-gray-600 text-[13px] mt-1">Create and manage shared wallets and 4Ward Savings for families, friends, societies, office expenses, and Ajo (thrift savings).</p>
                <ul className="text-[13px] text-gray-600 mt-3 space-y-1">
                  <li>✅ Available to all members</li>
                  <li>✅ Join up to 10 groups</li>
                  <li>✅ Create up to 5 groups as a Principal Member</li>
                </ul>
                <p className="text-[12px] text-gray-500 mt-3">Konnect is not responsible for how funds are managed within each group.</p>
                </div>
                <Button type="link" icon={<QuestionCircleOutlined />} onClick={() => setHowItWorksOpen(true)} className="font-gilroyBold whitespace-nowrap px-0">
                  How it works
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 p-4"><p className="font-gilroyBold text-[13px]">Up to 5 groups</p><p className=" text-[13px] text-gray-500">As a Principal</p></div>
                <div className="rounded-xl bg-gray-50 p-4"><p className="font-gilroyBold text-[13px]">Up to 10 groups</p><p className=" text-[13px] text-gray-500">As a member</p></div>
              </div>
              <Input size="large" value={name} onChange={(event) => setName(event.target.value)} placeholder="Group account name" />
              <Button type="primary" size="large" onClick={createGroup} className="bg-green-600 w-full">Create Group Account</Button>
            </section>
            <section className="space-y-3">
              <h2 className="text-xl font-gilroyBold">Your Groups</h2>
              {activeGroups.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 text-[13px] text-center text-gray-500">No active group memberships yet.</div>
              ) : (
                <AdminCollapseList items={activeGroups.map((item) => buildAdminCollapseItem({
                  key: item.group.id,
                  headerTitle: item.group.name,
                  headerTagText: `${item.members?.length || 0} members`,
                  headerTagColor: 'green',
                  children: (
                    <div className="space-y-3">
                      <p className="text-gray-500">Account {item.group.accountNumber}</p>
                      <div className="grid grid-cols-3 gap-3 text-gray-600">
                        <span><WalletOutlined className="mr-2" />₦{Number(item.group.walletBalance || 0).toLocaleString()}</span>
                        <span>4Ward ₦{Number(item.group.fourwardBalance || 0).toLocaleString()}</span>
                      <span><TeamOutlined className="mr-2" />{item.group.memberCount || item.members?.length || 0} members{item.group.isActive ? '' : ' · Pending activation'}</span>
                      </div>
                      <Button type="primary" onClick={() => openGroup(item)} className="bg-green-600">Manage Group</Button>
                    </div>
                  ),
                }))} />
              )}
            </section>
          </>
        ) : (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={closeGroup}>All Groups</Button>
            <div><h2 className="text-2xl font-gilroyBold">{selectedGroup?.group?.name}</h2><p className="text-gray-500">Account {selectedGroup?.group?.accountNumber}</p></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl bg-green-50 p-4"><p className="text-gray-600 text-[13px]">Group Wallet</p><p className="font-gilroyBold text-xl">₦{Number(selectedGroup?.group?.walletBalance || 0).toLocaleString()}</p></div>
              <div className="rounded-xl bg-blue-50 p-4"><p className="text-gray-600 text-[13px]">Group 4Ward</p><p className="font-gilroyBold text-xl">₦{Number(selectedGroup?.group?.fourwardBalance || 0).toLocaleString()}</p></div>
              <div className="rounded-xl bg-gray-50 p-4"><p className="text-gray-600 text-[13px]">Members</p><p className="font-gilroyBold text-xl">{selectedGroup?.members?.length || 0}</p></div>
            </div>
            {(selectedGroup?.membership?.role === 'PRINCIPAL' || selectedGroup?.membership?.role === 'SUB_PRINCIPAL') && <div className="border-t border-gray-100 pt-5 space-y-3">
              <h3 className="font-gilroyBold text-xl">Invite Members</h3>
              <Input size="large" value={memberId} onChange={(event) => setMemberId(event.target.value)} placeholder="Enter member Konnect ID" />
              <Select className="w-full" size="large" value={role} onChange={setRole} options={[{ value: 'MEMBER', label: 'Member' }, { value: 'SUB_PRINCIPAL', label: 'Sub Principal' }]} />
              <Button type="primary" size="large" onClick={addMember} className="bg-green-600">Invite Member</Button>
            </div>}
            <div className="border-t border-gray-100 pt-5 space-y-3">
              <h3 className="font-gilroyBold text-xl">Members</h3>
              {selectedGroup?.members?.length ? (
                <AdminCollapseList items={selectedGroup.members.map((member) => buildAdminCollapseItem({
                  key: member.membership.id,
                  headerTitle: `${member.firstName} ${member.lastName}`,
                  headerTagText: member.membership.status,
                  headerTagColor: member.membership.status === 'ACCEPTED' ? 'green' : 'orange',
                  items: [
                    { label: 'Konnect ID', value: member.accountId || '—' },
                    { label: 'Role', value: member.membership.role || 'MEMBER' },
                  ],
                }))} />
              ) : <p className="text-gray-500">No members yet.</p>}
            </div>
            <div className="border-t border-gray-100 pt-5 space-y-3">
              <h3 className="font-gilroyBold text-xl">Move Group Wallet to Group 4Ward</h3>
              <p className="text-gray-500 text-[13px]">Move funds between the group balances without using a personal wallet.</p>
              <Input size="large" type="number" value={conversionAmount} onChange={(event) => setConversionAmount(event.target.value)} placeholder="Amount" />
              <Button onClick={convertWalletToFourward} size="large" type="primary" className="bg-green-600">Move to Group 4Ward</Button>
            </div>
            <div className="border-t border-gray-100 pt-5 space-y-3">
              <h3 className="font-gilroyBold text-xl">Request Withdrawal</h3>
              <p className="text-gray-500">Available balance: ₦{Number(withdrawalDestination === 'FOURWARD' ? selectedGroup?.group?.fourwardBalance || 0 : selectedGroup?.group?.walletBalance || 0).toLocaleString()}</p>
              {selectedGroup?.pendingWithdrawals?.length > 0 && <p className="text-amber-600 text-[13px]">{selectedGroup.pendingWithdrawals.length} withdrawal request(s) pending approval.</p>}
              <Input size="large" type="number" value={withdrawalAmount} onChange={(event) => setWithdrawalAmount(event.target.value)} placeholder="Amount" />
              <Select className="w-full" size="large" value={withdrawalDestination} onChange={setWithdrawalDestination} options={[{ value: 'PERSONAL_WALLET', label: 'Requester personal wallet' }, { value: 'FOURWARD', label: 'Requester 4Ward' }]} />
              <Button onClick={requestWithdrawal} size="large">Submit Withdrawal Request</Button>
            </div>
            <div className="border-t border-gray-100 pt-5 space-y-3">
              <h3 className="font-gilroyBold text-xl">Group Wallet & 4Ward History</h3>
              {groupHistory.length === 0 ? <p className="text-gray-500">No group transactions yet.</p> : (
                <div className="space-y-2">
                  {groupHistory.map((transaction) => (
                    <div key={transaction.id} className="flex justify-between gap-3 border-b border-gray-100 py-2">
                      <div><p className="font-gilroyBold">{transaction.description}</p><p className="text-gray-500">{transaction.balanceType} · {new Date(transaction.createdAt).toLocaleString()}</p></div>
                      <span className={transaction.type === 'DEBIT' ? 'text-red-600' : 'text-green-600'}>{transaction.type === 'DEBIT' ? '-' : '+'}₦{Number(transaction.amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Modal
        title="How Group Accounts Work"
        open={howItWorksOpen}
        onCancel={() => setHowItWorksOpen(false)}
        footer={null}
        centered
      >
        <ol className="space-y-4 text-gray-700 list-decimal pl-5 pt-2">
          <li>A Principal creates the group and invites members. Only Principals and Sub-Principals can add new members.</li>
          <li>All registered members can join and contribute to the group.</li>
          <li>Every group gets a shared Wallet, Group 4Ward Account, and transaction history.</li>
          <li>Members can fund the group, view balances, and track savings and financial goals.</li>
          <li>Funds saved in Group 4Ward appreciate in value, increasing the group's future purchasing power.</li>
          <li>Withdrawals require approval from two members: a Principal or Sub-Principal plus one additional member, while all members receive real-time notifications and full visibility of group activities.</li>
        </ol>
      </Modal>
      <FooterDisplay />
    </div>
  );
};

export default GroupAccountPage;
